export enum BannerStyleStatus {
    Announcement = 0,
    Alert = 1,
}

export enum BannerColorStatus {
    Info = 0,
    Success = 1,
    Warning = 2,
    Danger = 3,
}

export enum BannerFrequencyStatus {
    UntilDismissed = 0,
    EveryPageLoad = 1,
}

export type BannerScope = 'inportal' | 'signin';

export interface BannerSettings {
    enabled: boolean;
    style: BannerStyleStatus;
    color: BannerColorStatus;
    messageHtml: string;
    alwaysOn: boolean;
    startDate: string;
    endDate: string;
    frequency: BannerFrequencyStatus;
    version: number;
}

export const CUSTOM_BANNER_SETTINGS_KEY = 'custom-banner-settings';
export const SIGNIN_BANNER_SETTINGS_KEY = 'signin-banner-settings';

export const parseBannerSettings = (raw: unknown): BannerSettings | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    const value = raw as Partial<BannerSettings>;
    return {
        enabled: Boolean(value.enabled),
        style: value.style ?? BannerStyleStatus.Announcement,
        color: value.color ?? BannerColorStatus.Info,
        messageHtml: typeof value.messageHtml === 'string' ? value.messageHtml : '',
        alwaysOn: value.alwaysOn !== false,
        startDate: typeof value.startDate === 'string' ? value.startDate : '',
        endDate: typeof value.endDate === 'string' ? value.endDate : '',
        frequency: value.frequency ?? BannerFrequencyStatus.UntilDismissed,
        version: typeof value.version === 'number' ? value.version : 1,
    };
};

/*
 * Tenant-local calendar day from timezoneOffsetMinutes. This is a DST-naive snapshot:
 * boundary days can be off by ±1 hour (at most one calendar day). timezoneCode is worse
 * (four hardcoded US zones defaulting to Eastern) and is intentionally not used.
 */
export const getTenantToday = (timezoneOffsetMinutes?: number): string => {
    const minutes = typeof timezoneOffsetMinutes === 'number' ? timezoneOffsetMinutes : 0;
    const shifted = new Date(Date.now() + minutes * 60 * 1000);
    const year = shifted.getUTCFullYear();
    const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
    const day = String(shifted.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const isBannerInSchedule = (
    settings: BannerSettings,
    timezoneOffsetMinutes?: number
): boolean => {
    if (settings.alwaysOn) {
        return true;
    }

    const today = getTenantToday(timezoneOffsetMinutes);
    const start = settings.startDate.trim();
    const end = settings.endDate.trim();
    if (!start || !end) {
        return false;
    }

    return start <= today && today <= end;
};
